import { useState } from "react";
import type { MediaFilters, SortKey } from "@/lib/media";
import { sortOptions } from "@/lib/media";

type Dimension = "genres" | "regions" | "years";

interface FilterBarProps {
  options: { genres: string[]; regions: string[]; years: string[] };
  filters: MediaFilters;
  onToggle: (dimension: Dimension, value: string) => void;
  onReset: () => void;
  sort?: SortKey;
  onSortChange?: (sort: SortKey) => void;
}

const groups: { key: Dimension; label: string }[] = [
  { key: "genres", label: "类型" },
  { key: "regions", label: "地区" },
  { key: "years", label: "年份" },
];

export default function FilterBar({
  options,
  filters,
  onToggle,
  onReset,
  sort,
  onSortChange,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const activeCount =
    filters.genres.length + filters.regions.length + filters.years.length;

  const renderGroup = (key: Dimension, label: string) => {
    const values = options[key];
    if (values.length === 0) return null;
    return (
      <div key={key} className="flex flex-col gap-2 border-b border-background-200 py-3 last:border-b-0 sm:flex-row sm:items-start sm:gap-4">
        <span className="w-12 shrink-0 pt-1 text-sm text-foreground-500">{label}</span>
        <div className="flex flex-wrap gap-2">
          {values.map((value) => {
            const active = filters[key].includes(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => onToggle(key, value)}
                className={`rounded-full px-3 py-1 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600"
                }`}
              >
                {value}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const sortControl = onSortChange ? (
    <div className="flex items-center gap-2">
      <span className="text-sm text-foreground-500">排序</span>
      <div className="flex flex-wrap gap-2">
        {sortOptions.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => onSortChange(option.key)}
            className={`rounded-full px-3 py-1 text-xs transition-colors cursor-pointer whitespace-nowrap ${
              sort === option.key
                ? "bg-accent-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-accent-50 hover:text-accent-700"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  const resetButton = (
    <button
      type="button"
      onClick={onReset}
      className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
    >
      <i className="ri-refresh-line" aria-hidden="true"></i>
      重置筛选
    </button>
  );

  return (
    <>
      {/* 桌面端筛选面板 */}
      <div className="hidden rounded-lg border border-background-200 bg-background-50 p-4 md:block">
        <div className="mb-3 flex items-center justify-between gap-4">
          {sortControl}
          {resetButton}
        </div>
        {groups.map((group) => renderGroup(group.key, group.label))}
      </div>

      {/* 移动端筛选按钮 */}
      <div className="flex items-center gap-2 md:hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex flex-1 items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 py-2.5 text-sm text-foreground-700 cursor-pointer"
        >
          <i className="ri-filter-3-line" aria-hidden="true"></i>
          筛选{activeCount > 0 ? `（${activeCount}）` : ""}
        </button>
        {onSortChange ? (
          <select
            value={sort}
            onChange={(event) => onSortChange(event.target.value as SortKey)}
            aria-label="排序方式"
            className="flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-700 outline-none cursor-pointer"
          >
            {sortOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {/* 移动端筛选覆盖层 */}
      {open ? (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-foreground-950/50 cursor-pointer"
          ></button>
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="关闭"
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl" aria-hidden="true"></i>
              </button>
            </div>
            {groups.map((group) => renderGroup(group.key, group.label))}
            <div className="mt-4 flex gap-3">
              {resetButton}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 rounded-md bg-primary-500 py-2.5 text-sm font-semibold text-background-50 cursor-pointer"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}